import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useRating, type RatingMode, type RatingPeriod } from "@/entities/rating";
import { ROUTES } from "@/shared/config";
import { Icon, Segmented, type SegmentedOption } from "@/shared/ui";
import { RatingMyPosition, RatingTable } from "@/widgets/rating-table";
import { RatingPodium } from "@/widgets/rating-podium";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Рейтинг участников.
 *
 * Подэкран профиля, а не вкладка: в нижней навигации пять фиксированных
 * пунктов, и в пакете дизайна на этом экране подсвечен именно «Профиль».
 * Отсюда и квадрат «назад» в шапке.
 *
 * Режим и период уходят в запрос — ранжирование делает база. Отфильтровать
 * пришедшие десять строк на клиенте нельзя: «первые десять за неделю» — это
 * другая выборка, а не подмножество первых десяти за день.
 */
const MODES: readonly RatingMode[] = ["earnings", "robots"];
const PERIODS: readonly RatingPeriod[] = ["day", "week", "month", "all"];

export function RatingPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<RatingMode>("earnings");
  const [period, setPeriod] = useState<RatingPeriod>("day");

  const { data, isPending, isError, refetch } = useRating(mode, period);

  const modeOptions = useMemo<readonly SegmentedOption<RatingMode>[]>(
    () => [
      { value: MODES[0]!, label: t("rating.mode.earnings"), icon: "trendUp" },
      { value: MODES[1]!, label: t("rating.mode.robots"), icon: "robot" },
    ],
    [t],
  );

  const periodOptions = useMemo<readonly SegmentedOption<RatingPeriod>[]>(
    () => PERIODS.map((item) => ({ value: item, label: t(`rating.period.${item}`) })),
    [t],
  );

  return (
    <div className="flex flex-col gap-1 px-[var(--layout-gutter)] py-3">
      <TopBar
        title={t("rating.title")}
        subtitle={t("rating.subtitle")}
        back={{ to: ROUTES.profile, label: t("common.back") }}
      />

      <div className="mt-2 flex flex-col gap-1">
        <Segmented
          options={modeOptions}
          value={mode}
          onChange={setMode}
          ariaLabel={t("rating.modeLabel")}
        />
        <Segmented
          options={periodOptions}
          value={period}
          onChange={setPeriod}
          ariaLabel={t("rating.periodLabel")}
        />
      </div>

      {isError ? (
        <ScreenState
          tone="loss"
          icon="warningFill"
          title={t("states.error.title")}
          body={t("states.error.body")}
          action={{ label: t("states.error.action"), onClick: () => void refetch() }}
        />
      ) : isPending ? (
        <ScreenSkeleton blocks={[178, 156, 60]} />
      ) : data.top.length === 0 ? (
        <ScreenState
          tone="info"
          icon="trayFill"
          title={t("rating.empty.title")}
          body={t("rating.empty.body")}
        />
      ) : (
        <>
          {/* Приватность (доработки §5). Согласие спрашивается в профиле, но
              объяснить обезличенную таблицу надо здесь: молча анонимный
              рейтинг читается как поломка, а не как приватность. Плашка
              исчезает сама, как только человек включил показ. */}
          {data.me.public_rating_opt_in ? null : (
            <Link
              to={ROUTES.profilePrivacy}
              data-haptic="select"
              className="press-row mt-2 flex items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-background-elevated px-4 py-2.5"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Icon name="shieldCheck" size={16} className="shrink-0 text-text-tertiary" />
                <span className="truncate text-caption font-normal text-text-secondary">
                  {t("rating.privacy.hint")}
                </span>
              </span>
              <span className="shrink-0 text-caption font-semibold text-action-primary">
                {t("rating.privacy.action")}
              </span>
            </Link>
          )}
          <RatingPodium top={data.top} />
          <RatingTable rows={data.rows} />
        </>
      )}

      {isPending || isError ? null : (
        <div className="mt-2">
          <RatingMyPosition me={data.me} />
        </div>
      )}
    </div>
  );
}
